import React from 'react';
import { StyleSheet, View } from 'react-native';
import type { AddressType } from '@sweetgreen/garnish';
import { Button, useResponsive } from '@sweetgreen/garnish';
import {
  BodyText,
  capitalizeFirstLetter,
  Card,
  theme,
} from '@sweetgreen/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { ignoreTimezone } from '@order/utils';

import {
  useKustomerAvailability,
  useKustomerContactUsHandler,
} from '../../integrations/kustomer';
import {
  OrderCancellationModal,
  useOrderCancellationModal,
} from '../OrderCancellationModal';
import { OrderCardImage } from './components';

export const OrderCard = (props: OrderCardProps) => {
  const {
    orderId,
    restaurantId,
    isCanceled,
    isCancellable,
    onPress,
    onOrderCancellationSuccess,
  } = props;

  const shouldRenderCanceledLabel = isCanceled && !isCancellable;

  return (
    <Card onPress={onPress}>
      <View style={styles.content}>
        <OrderCardImage {...props} />
        <View style={styles.info}>
          <OrderCardTitle {...props} />
          <OrderCardLocation {...props} />
          <OrderCardDescription {...props} />

          <View style={styles.priceAndStatusContainer}>
            {shouldRenderCanceledLabel ? <OrderCardCanceledLabel /> : null}
            <OrderCardTotal {...props} />
          </View>

          {isCancellable ? (
            <OrderCardQuickCancel
              orderId={orderId}
              restaurantId={restaurantId}
              onOrderCancellationSuccess={onOrderCancellationSuccess}
            />
          ) : null}
        </View>
      </View>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardTitle = ({
  wantedTime,
  orderType,
}: Pick<OrderCardProps, 'wantedTime' | 'orderType'>) => {
  const { t } = useLocalizationContext();

  const normalizedWantedTime = ignoreTimezone(wantedTime);
  const capitalizedOrderType = capitalizeFirstLetter(orderType);
  const title = normalizedWantedTime
    ? t('account.order-card.title', {
        wantedTime: normalizedWantedTime,
        orderType: capitalizedOrderType,
      })
    : capitalizedOrderType;

  return <BodyText bold>{title}</BodyText>;
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardLocation = ({
  orderType,
  restaurantName,
  addressName,
}: Pick<OrderCardProps, 'orderType' | 'restaurantName' | 'addressName'>) => (
  <BodyText sizeMatch={['14', '16']}>
    {orderType === 'delivery' && addressName
      ? capitalizeFirstLetter(addressName)
      : restaurantName}
  </BodyText>
);
// ────────────────────────────────────────────────────────────────────────────────

const OrderCardDescription = ({
  description,
}: Pick<OrderCardProps, 'description'>) => (
  <View style={styles.description}>
    <BodyText numberOfLines={2} ellipsizeMode="tail" sizeMatch={['14', '16']}>
      {description}
    </BodyText>
  </View>
);

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardTotal = ({ total }: Pick<OrderCardProps, 'total'>) => {
  const { formatPrice } = useLocalizationContext();

  return (
    <BodyText sizeMatch={['14', '16']}>{formatPrice(total, 'USD')}</BodyText>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardCanceledLabel = () => {
  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  return (
    <BodyText
      bold
      style={match([styles.isCanceledLabelXS, styles.isCanceledLabelSM])}
      sizeMatch={['14', '16']}
    >
      {t('account.orders.canceled')}
    </BodyText>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardQuickCancel = (props: OrderCardQuickCancelProps) => {
  const { orderId, restaurantId, onOrderCancellationSuccess } = props;

  const { t } = useLocalizationContext();
  const { handleContactUs, handlingContactUs } = useKustomerContactUsHandler(
    useKustomerAvailability(),
    'order-history',
  );
  const isInAppOrderCancellationEnabled = useFeatureFlag(
    'CELS-1476-in-app-order-cancellation',
  );

  // ─────────────────────────────────────────────────────────────────────

  const {
    isOrderCancellationModalVisible,
    showOrderCancellationModal,
    hideOrderCancellationModal,
  } = useOrderCancellationModal();

  const isLoading = isInAppOrderCancellationEnabled ? false : handlingContactUs;
  const onPress = isInAppOrderCancellationEnabled
    ? showOrderCancellationModal
    : handleContactUs;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <View style={styles.cancelButtonContainer}>
        <Button palette="secondary" isLoading={isLoading} onPress={onPress}>
          {t('account.orders.cancel')}
        </Button>
      </View>

      {isInAppOrderCancellationEnabled ? (
        <OrderCancellationModal
          orderId={orderId}
          restaurantId={restaurantId}
          isVisible={isOrderCancellationModalVisible}
          onSuccess={onOrderCancellationSuccess}
          onRequestClose={hideOrderCancellationModal}
        />
      ) : null}
    </>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing['6'],
  },
  info: {
    flex: 1,
    marginLeft: theme.spacing['4'],
  },
  description: {
    marginVertical: theme.spacing['2'],
  },
  cancelButtonContainer: {
    alignItems: 'flex-start',
    marginTop: theme.spacing['2'],
  },
  priceAndStatusContainer: {
    flexDirection: 'row',
    gap: theme.spacing['1'],
  },
  isCanceledLabelXS: {
    lineHeight: 20, // to match other text elements baseline
  },
  isCanceledLabelSM: {
    lineHeight: 24, // to match other text elements baseline
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────────

type OrderCardProps = Readonly<{
  orderId: string;
  orderType: AddressType;
  wantedTime: string;
  restaurantId: string;
  restaurantName?: string;
  addressName?: string;
  description: string;
  total: number;
  assetUrl: string;
  isCanceled: boolean;
  isCancellable?: boolean;
  onPress: () => void;
  onOrderCancellationSuccess: (orderId: string) => void;
}>;

type OrderCardQuickCancelProps = Readonly<{
  orderId: string;
  restaurantId: string;
  onOrderCancellationSuccess: (orderId: string) => void;
}>;
